/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : viewDynamicFormTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React from "react";
import PropTypes from "prop-types";
import { fontSizeDetails } from "../../../config/appTheme";

const EDIT_TYPES = { DDH: 100, SNOW: 200, BOTH: 300 };
function getKeyByValue(value) {
  const type = [
    { key: "SOFTWARE MODULE", value: 100 },
    { key: "EE COMPONENT", value: 200 },
    { key: "VEHICLE COMPONENT", value: 300 },
  ]?.find((item) => item.value === value);
  return type ? type.key : "Key not found";
}

function getFieldColor(editType) {
  if (editType === EDIT_TYPES?.DDH) return "blue";
  if (editType === EDIT_TYPES?.SNOW) return "grey";
  return "black";
}

function ViewDynammicFormDetailsTemplate({
  data,
  open,
  handleClose,
  selectedHeaders,
}) {

  console.log("View Data", data);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: fontSizeDetails.subtitle2.fontSize,
          }}
        >
          Name of the Form :{data?.name}
        </Typography>
        <Box sx={{ display: "flex" }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: fontSizeDetails.subtitle2.fontSize,
            }}
          >
            Type Of Form
          </Typography>
          <strong>:</strong>
          <Typography sx={{ fontSize: fontSizeDetails.subtitle1.fontSize }}>
            {getKeyByValue(data?.type)}
          </Typography>
        </Box>
      </Box>
      <Paper
        sx={{
          border: "1px solid grey",
          borderRadius: 1,
          flexGrow: 1,
          mt: "10px",
        }}
      >
        <Box
          sx={{
            p: 1.4,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {data?.headers?.map((item, index) => (
            <Box key={item?.id || "header-" + index} sx={{ visibility: "visible" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "15px",
                }}
              >
                <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                  {" "}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: fontSizeDetails.body1.fontSize,
                    }}
                  >
                    {item?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: fontSizeDetails.body1.fontSize }}
                  >
                    {item?.sub_title}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ border: "1px solid lightGrey" }} />
        <Box sx={{ minHeight: "250px", m: 2.5 }}>
          {data?.fields?.map((field, index) => (
            <Box
              key={field?.id || "field-" + index}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                alignItems: "center",
                mb: 1.5,
                columnGap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: fontSizeDetails.body1.fontSize,
                    minWidth: 20,
                    textAlign: "right",
                  }}
                >
                  {field?.sequence}.
                </Typography>

                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: fontSizeDetails.body1.fontSize,
                  }}
                  color={getFieldColor(field?.editType)}
                >
                  {field?.field_name}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: fontSizeDetails.body1.fontSize,
                  wordBreak: "break-word",
                }}
              >
                {field?.value || "-"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: 1.4,
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
          }}
        >
          {data?.signatures?.map((item, index) => (
            <Box
              key={item?.id || "sig-" + index}
              sx={{
                visibility: "visible",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  ml: "10px",
                }}
              ></Box>
              <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                {" "}
                <Typography sx={{ fontSize: fontSizeDetails.body1.fontSize }}>
                  {item?.title}
                </Typography>
                <Typography sx={{ fontSize: fontSizeDetails.body1.fontSize }}>
                  {item?.sub_title}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Divider sx={{ border: "1px solid lightGrey" }} />
        <Box
          sx={{
            p: 1.4,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {data?.footers?.map((item, index) => (
            <Box
              key={item?.id || "footer-" + index}
              sx={{
                visibility: "visible",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "15px",
                }}
              >
                <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                  {" "}
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: fontSizeDetails.body1.fontSize,
                    }}
                  >
                    {item?.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: fontSizeDetails.body1.fontSize }}
                  >
                    {item?.sub_title}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
}


ViewDynammicFormDetailsTemplate.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.any,
    headers: PropTypes.array,
    fields: PropTypes.array,
    signatures: PropTypes.array,
    footers: PropTypes.array,
  }),
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selectedHeaders: PropTypes.any,
};

export default ViewDynammicFormDetailsTemplate;
